// Pure helpers for the Marketing panel: tag field validation (the same
// MARKETING_TAGS patterns studio checks before saving), the consent banner
// preview, the WhatsApp preview, attribution totals, the UTM link builder and
// Search Console labels. No React/store imports so it stays unit-testable.
import { MARKETING_TAGS, whatsAppDigits, whatsAppUrl, type ConsentCategory } from '@dunea/shared';
import type {
  AttributionRow,
  ConsentSettings,
  SearchConsoleStatus,
  TagConfigView,
  TagInput,
  WhatsAppSettings,
} from '~/lib/api/marketing';
import type { Tone } from '~/components/workbench/tools/payments/payments-model';

export { apiErrorMessage, formatMinor } from '~/components/workbench/tools/payments/payments-model';

// ─── dates ───

const dateFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });

export function formatDate(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }

  const d = new Date(iso);

  return Number.isNaN(d.getTime()) ? '' : dateFormat.format(d);
}

// ─── tracking tags ───

export type TagSpec = (typeof MARKETING_TAGS)[number];
export type TagField = TagSpec['fields'][number];

export const TAG_SPECS: readonly TagSpec[] = MARKETING_TAGS;

export function consentCategoryLabel(category: ConsentCategory): string {
  return category === 'analytics' ? 'Analytics' : 'Marketing';
}

/** Inline error for one field value, or null when empty (required is checked separately) or valid. */
export function fieldError(field: TagField, value: string): string | null {
  const v = value.trim();

  if (!v) {
    return null;
  }

  return new RegExp(field.pattern).test(v) ? null : `Doesn't look right. Expected something like ${field.placeholder}`;
}

/** What the tag card edits. Secrets start empty (write-only); `clear` lists stored secrets to remove. */
export interface TagDraft {
  enabled: boolean;
  values: Record<string, string>;
  secrets: Record<string, string>;
  clear: string[];
}

export function tagDraft(spec: TagSpec, view: TagConfigView | undefined): TagDraft {
  const values: Record<string, string> = {};

  for (const f of spec.fields) {
    if (!f.secret) {
      values[f.key] = view?.values[f.key] ?? '';
    }
  }

  return { enabled: view?.enabled ?? false, values, secrets: {}, clear: [] };
}

/** Field key → problem. Mirrors studio's validateTagInput so Save only sends what it accepts. */
export function tagProblems(spec: TagSpec, draft: TagDraft): Record<string, string> {
  const problems: Record<string, string> = {};

  for (const f of spec.fields) {
    const value = (f.secret ? draft.secrets[f.key] : draft.values[f.key]) ?? '';
    const err = fieldError(f, value);

    if (err) {
      problems[f.key] = err;
    } else if (f.required && !f.secret && draft.enabled && !value.trim()) {
      problems[f.key] = `Required to turn on ${spec.title}`;
    }
  }

  return problems;
}

/** The PUT body: public values, plus a secrets patch only when a secret was typed or cleared. */
export function tagInput(spec: TagSpec, draft: TagDraft): TagInput {
  const values: Record<string, string> = {};
  const secrets: Record<string, string | null> = {};

  for (const f of spec.fields) {
    if (f.secret) {
      const typed = draft.secrets[f.key]?.trim();

      if (typed) {
        secrets[f.key] = typed;
      } else if (draft.clear.includes(f.key)) {
        secrets[f.key] = null;
      }
    } else if (draft.values[f.key]?.trim()) {
      values[f.key] = draft.values[f.key].trim();
    }
  }

  return { enabled: draft.enabled, values, ...(Object.keys(secrets).length ? { secrets } : {}) };
}

export function tagDirty(spec: TagSpec, draft: TagDraft, view: TagConfigView | undefined): boolean {
  const saved = tagDraft(spec, view);

  if (draft.enabled !== saved.enabled || draft.clear.length > 0) {
    return true;
  }

  return spec.fields.some((f) =>
    f.secret
      ? Boolean(draft.secrets[f.key]?.trim())
      : (draft.values[f.key] ?? '').trim() !== (saved.values[f.key] ?? '').trim(),
  );
}

export function tagStatusMeta(view: TagConfigView | undefined): { label: string; tone: Tone } {
  if (!view) {
    return { label: 'Not set up', tone: 'muted' };
  }

  return view.enabled ? { label: 'On', tone: 'success' } : { label: 'Off', tone: 'muted' };
}

/** Tags that can send server-side conversions (GA4 API secret, Meta access token) and have one saved. */
export function serverSideReady(views: readonly TagConfigView[]): string[] {
  const out: string[] = [];

  for (const spec of TAG_SPECS) {
    const view = views.find((v) => v.provider === spec.id);
    const secret = spec.fields.find((f) => f.secret);

    if (view?.enabled && secret && view.secretsSet.includes(secret.key)) {
      out.push(spec.title);
    }
  }

  return out;
}

// ─── consent banner ───

// Same defaults as the published-site runtime (packages/shared marketing-runtime).
export const CONSENT_DEFAULT_TITLE = 'Cookies & privacy';
export const CONSENT_DEFAULT_MESSAGE = 'We use cookies for analytics and to measure our ads. Choose what you allow.';

const SAFE_URL = /^(https?:\/\/[^\s<>"'`]+|\/(?!\/)[^\s<>"'`]*)$/i;

export function isValidPrivacyUrl(url: string): boolean {
  const v = url.trim();
  return !v || (v.length <= 500 && SAFE_URL.test(v));
}

export interface ConsentPreview {
  title: string;
  message: string;
  showPrivacyLink: boolean;
  buttons: string[];
  align: 'center' | 'left' | 'right';
}

export function consentPreview(
  c: Pick<ConsentSettings, 'title' | 'message' | 'privacyUrl' | 'position'>,
): ConsentPreview {
  const privacy = c.privacyUrl?.trim() ?? '';

  return {
    title: c.title?.trim() || CONSENT_DEFAULT_TITLE,
    message: c.message?.trim() || CONSENT_DEFAULT_MESSAGE,
    showPrivacyLink: Boolean(privacy) && isValidPrivacyUrl(privacy),
    buttons: ['Reject', 'Customize', 'Accept all'],
    align: c.position === 'bottom-left' ? 'left' : c.position === 'bottom-right' ? 'right' : 'center',
  };
}

/** What the chosen mode means for the visitor, one line. */
export function consentModeHint(enabled: boolean, mode: ConsentSettings['mode']): string {
  if (!enabled) {
    return 'No banner: every tag loads for every visitor. Only do this where no consent law applies to you.';
  }

  return mode === 'opt-in'
    ? 'Nothing loads until the visitor accepts. Recommended for the EU, UK and India (DPDP).'
    : 'Tags load straight away and the banner lets visitors opt out.';
}

export const CONSENT_POSITIONS = [
  ['bottom', 'Bottom, full width'],
  ['bottom-left', 'Bottom left'],
  ['bottom-right', 'Bottom right'],
] as const;

// ─── WhatsApp ───

export function whatsAppPhoneError(phone: string): string | null {
  if (!phone.trim()) {
    return null;
  }

  return whatsAppDigits(phone) ? null : 'Use the full number with country code, 8–15 digits (e.g. 919876543210).';
}

export function whatsAppPreview(s: Pick<WhatsAppSettings, 'phone' | 'message' | 'label'>): {
  label: string;
  url: string | null;
} {
  return {
    label: (s.label?.trim() || 'Chat on WhatsApp').slice(0, 40),
    url: whatsAppUrl({ phone: s.phone, message: s.message }),
  };
}

// ─── attribution ───

export const ATTRIBUTION_DAYS = [
  ['7', '7 days'],
  ['30', '30 days'],
  ['90', '90 days'],
] as const;

/** Orders across rows and revenue per currency (rows may mix currencies; never summed across them). */
export function attributionTotals(rows: readonly AttributionRow[]): {
  orders: number;
  revenue: { currency: string; amount: number }[];
} {
  const byCurrency = new Map<string, number>();
  let orders = 0;

  for (const r of rows) {
    orders += r.orders;
    byCurrency.set(r.currency, (byCurrency.get(r.currency) ?? 0) + r.revenue);
  }

  return {
    orders,
    revenue: [...byCurrency.entries()]
      .map(([currency, amount]) => ({ currency, amount }))
      .sort((a, b) => b.amount - a.amount || a.currency.localeCompare(b.currency)),
  };
}

// ─── UTM links ───

export const UTM_PLACEHOLDER_URL = 'https://your-site.com/';
export const UTM_EXAMPLE = { source: 'instagram', medium: 'social', campaign: 'diwali' } as const;

export interface UtmParams {
  source: string;
  medium: string;
  campaign: string;
  term?: string;
  content?: string;
}

const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'] as const;

/**
 * `base` + utm_* parameters, each value percent-encoded. Existing query
 * parameters and the hash are kept; existing utm_* are replaced. Null when the
 * base isn't an http(s) URL.
 */
export function buildUtmUrl(base: string, params: UtmParams): string | null {
  let url: URL;

  try {
    url = new URL(base.trim());
  } catch {
    return null;
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return null;
  }

  for (const key of UTM_KEYS) {
    url.searchParams.delete(`utm_${key}`);
  }

  const existing = url.search.replace(/^\?/, '');
  const added = UTM_KEYS.flatMap((key) => {
    const value = params[key]?.trim();
    return value ? [`utm_${key}=${encodeURIComponent(value)}`] : [];
  });

  url.search = [existing, ...added].filter(Boolean).join('&');

  return url.toString();
}

/** The published site's address from the Search Console status (its siteUrl falls back to the live deploy). */
export function siteBaseUrl(status: Pick<SearchConsoleStatus, 'siteUrl'> | null | undefined): string | null {
  const url = status?.siteUrl?.trim();
  return url && /^https?:\/\//i.test(url) ? url : null;
}

// ─── Search Console ───

const SC_PARAM = 'searchConsole';

/** The OAuth outcome Google's redirect appended (`?searchConsole=connected|error`), if any. */
export function searchConsoleReturn(search: string): 'connected' | 'error' | null {
  const value = new URLSearchParams(search).get(SC_PARAM);
  return value === 'connected' || value === 'error' ? value : null;
}

/** The current URL without the OAuth outcome (and its panel marker), for history.replaceState after showing it. */
export function withoutSearchConsoleParam(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(SC_PARAM);

  if (url.searchParams.get('panel') === 'marketing') {
    url.searchParams.delete('panel');
  }

  return url.toString();
}

/** Where Google sends the owner back: this page, marked to reopen the Marketing panel. */
export function connectReturnUrl(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(SC_PARAM);
  url.searchParams.set('panel', 'marketing');

  return url.toString();
}

export type SearchConsoleStage = 'disconnected' | 'unverified' | 'verified';

export function searchConsoleStage(status: Pick<SearchConsoleStatus, 'connected' | 'verified'>): SearchConsoleStage {
  if (!status.connected) {
    return 'disconnected';
  }

  return status.verified ? 'verified' : 'unverified';
}

/** CTR is 0..1 from Google. */
export function ctrLabel(ctr: number): string {
  if (!Number.isFinite(ctr) || ctr <= 0) {
    return '0%';
  }

  const pct = ctr * 100;

  return `${pct < 10 ? pct.toFixed(1) : Math.round(pct)}%`;
}

export function positionLabel(position: number): string {
  return Number.isFinite(position) && position > 0 ? position.toFixed(1) : '—';
}

export function compactNumber(n: number): string {
  return new Intl.NumberFormat('en', {
    notation: n >= 10000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(n);
}

/** "https://example.com/blog/post" → "/blog/post" when it's on the site, else the URL as is. */
export function pagePath(page: string, siteUrl: string | null): string {
  if (siteUrl && page.startsWith(siteUrl.replace(/\/+$/, ''))) {
    return page.slice(siteUrl.replace(/\/+$/, '').length) || '/';
  }

  return page;
}
